import React, { useState, useRef, useCallback } from 'react';
import './ComplianceUpload.css';
import './BulkUpload.css';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API_KEY = ''; // Step 41 — auth via httpOnly cookie; this stub keeps existing 'Bearer ' headers harmless

const DOC_TYPE_LABELS = {
  epc: 'EPC',
  gas_safety: 'Gas Safety',
  eicr: 'EICR',
};

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];

function ConfBadge({ level }) {
  if (!level || level === 'low') return <span className="conf-dot conf-low" data-testid="conf-low">Low</span>;
  if (level === 'medium') return <span className="conf-dot conf-medium" data-testid="conf-medium">Med</span>;
  return <span className="conf-dot conf-high" data-testid="conf-high">High</span>;
}

export default function BulkUpload({ onClose, onSaved }) {
  const [step, setStep] = useState('select');   // select | uploading | review | saving | done
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [results, setResults] = useState([]);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [editedRows, setEditedRows] = useState({});
  const [confirmedRows, setConfirmedRows] = useState({});
  const [error, setError] = useState(null);
  const [saveResult, setSaveResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [reExtractingRow, setReExtractingRow] = useState(null);
  const fileInputRef = useRef(null);

  const headers = { 'Authorization': `Bearer ${API_KEY}` };

  // --- File Selection ---
  const addFiles = (fileList) => {
    const newFiles = [];
    for (const f of fileList) {
      if (!ALLOWED_TYPES.includes(f.type)) continue;
      if (f.size > 20 * 1024 * 1024) continue;
      // Deduplicate by name+size
      if (!files.some(ex => ex.name === f.name && ex.size === f.size)) {
        newFiles.push(f);
      }
    }
    setFiles(prev => [...prev, ...newFiles]);
    setError(null);
  };

  const handleFileInput = (e) => { if (e.target.files) addFiles(e.target.files); };
  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  // --- Drag and Drop ---
  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  // --- Upload & Extract ---
  const handleBulkUpload = async () => {
    if (files.length === 0) return;
    setStep('uploading');
    setError(null);

    // Set initial progress
    const prog = {};
    files.forEach((f, i) => { prog[i] = { status: 'queued', pct: 0 }; });
    setUploadProgress({ ...prog });

    const formData = new FormData();
    files.forEach(f => formData.append('files', f));

    // Mark all as uploading
    files.forEach((_, i) => { prog[i] = { status: 'uploading', pct: 50 }; });
    setUploadProgress({ ...prog });

    try {
      const res = await fetch(`${API_BASE_URL}/api/compliance/bulk-upload`, {
        method: 'POST',
        headers,
        body: formData,
      });
      const resText = await res.text();
      const resData = resText ? JSON.parse(resText) : {};
      if (!res.ok) {
        throw new Error(resData.detail || 'Bulk upload failed');
      }

      // Mark all done
      files.forEach((_, i) => { prog[i] = { status: 'done', pct: 100 }; });
      setUploadProgress({ ...prog });

      setResults(resData.results || []);
      setPropertyOptions(resData.property_options || []);

      // Initialise edited rows from extraction results
      const edited = {};
      const confirmed = {};
      (resData.results || []).forEach((r, idx) => {
        if (!r.fields) return;
        edited[idx] = {
          upload_id: r.upload_id,
          property_id: r.fields.property_id?.value || '',
          doc_type: r.fields.doc_type?.value || '',
          expiry_date: r.fields.expiry_date?.value || '',
          issue_date: r.fields.issue_date?.value || '',
          issuing_company: r.fields.issuing_company?.value || '',
        };
        // Auto-confirm rows where BOTH property and doc_type are high confidence
        const propConf = r.fields.property_id?.confidence;
        const dtConf = r.fields.doc_type?.confidence;
        if (propConf === 'high' && dtConf === 'high' && r.fields.property_id?.value && r.fields.doc_type?.value) {
          confirmed[idx] = true;
        }
      });
      setEditedRows(edited);
      setConfirmedRows(confirmed);

      setStep('review');
    } catch (err) {
      setError(err.message);
      setStep('select');
    }
  };

  // --- Row Editing ---
  const updateRow = (idx, field, value) => {
    setEditedRows(prev => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: value },
    }));
    // Un-confirm row when edited (force re-confirm)
    setConfirmedRows(prev => { const c = { ...prev }; delete c[idx]; return c; });
  };

  const confirmRow = (idx) => {
    const row = editedRows[idx];
    if (!row?.property_id || !row?.doc_type) return; // Cannot confirm without required fields
    setConfirmedRows(prev => ({ ...prev, [idx]: true }));
  };

  const unconfirmRow = (idx) => {
    setConfirmedRows(prev => { const c = { ...prev }; delete c[idx]; return c; });
  };

  // --- Re-extract a single row ---
  const handleReExtractRow = async (idx) => {
    const r = results[idx];
    if (!r?.upload_id) return;
    setReExtractingRow(idx);
    setError(null);

    try {
      const row = editedRows[idx] || {};
      const res = await fetch(`${API_BASE_URL}/api/compliance/re-extract`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          upload_id: r.upload_id,
          hint_doc_type: row.doc_type || null,
          hint_property_id: row.property_id || null,
        }),
      });
      const resText = await res.text();
      const resData = resText ? JSON.parse(resText) : {};
      if (!res.ok) throw new Error(resData.detail || 'Re-extraction failed');

      // Update this row's results
      const newResults = [...results];
      newResults[idx] = { ...newResults[idx], fields: resData.fields, extraction_error: resData.extraction_error, tokens_used: resData.tokens_used, status: resData.extraction_error ? 'extraction_failed' : 'extracted' };
      setResults(newResults);

      // Re-init edited row from new extraction
      const f = resData.fields || {};
      setEditedRows(prev => ({
        ...prev,
        [idx]: {
          upload_id: r.upload_id,
          property_id: f.property_id?.value || '',
          doc_type: f.doc_type?.value || '',
          expiry_date: f.expiry_date?.value || '',
          issue_date: f.issue_date?.value || '',
          issuing_company: f.issuing_company?.value || '',
        }
      }));

      // Re-auto-confirm if both high
      const propConf = f.property_id?.confidence;
      const dtConf = f.doc_type?.confidence;
      if (propConf === 'high' && dtConf === 'high' && f.property_id?.value && f.doc_type?.value) {
        setConfirmedRows(prev => ({ ...prev, [idx]: true }));
      } else {
        setConfirmedRows(prev => { const c = { ...prev }; delete c[idx]; return c; });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setReExtractingRow(null);
    }
  };

  // --- Confirm All ---
  const allConfirmable = useCallback(() => {
    return results.every((r, idx) => {
      if (r.error || r.status === 'error') return true; // Skip error rows
      return confirmedRows[idx] === true;
    });
  }, [results, confirmedRows]);

  const confirmAllRows = () => {
    const newConfirmed = { ...confirmedRows };
    results.forEach((r, idx) => {
      if (r.error || r.status === 'error') return;
      const row = editedRows[idx];
      if (row?.property_id && row?.doc_type) {
        newConfirmed[idx] = true;
      }
    });
    setConfirmedRows(newConfirmed);
  };

  // --- Save ---
  const handleBulkSave = async () => {
    const items = [];
    results.forEach((r, idx) => {
      if (r.error || r.status === 'error') return;
      if (!confirmedRows[idx]) return;
      const row = editedRows[idx];
      if (!row?.property_id || !row?.doc_type) return;
      items.push({
        upload_id: row.upload_id,
        property_id: row.property_id,
        doc_type: row.doc_type,
        expiry_date: row.expiry_date || null,
        issue_date: row.issue_date || null,
        issuing_company: row.issuing_company || null,
        extraction_fields: r.fields,
      });
    });

    if (items.length === 0) return;
    setStep('saving');

    try {
      const res = await fetch(`${API_BASE_URL}/api/compliance/bulk-confirm`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      const resText = await res.text();
      const resData = resText ? JSON.parse(resText) : {};
      if (!res.ok) {
        throw new Error(resData.detail || 'Bulk save failed');
      }
      setSaveResult(resData);
      setStep('done');
      if (onSaved) onSaved();
    } catch (err) {
      setError(err.message);
      setStep('review');
    }
  };

  // --- Computed ---
  const confirmedCount = Object.keys(confirmedRows).length;
  const totalExtracted = results.filter(r => !r.error && r.status !== 'error').length;
  const needsAttentionCount = results.filter((r, idx) => {
    if (r.error || r.status === 'error') return false;
    const row = editedRows[idx];
    return !row?.property_id || !row?.doc_type;
  }).length;

  // --- Render: File Selection ---
  const renderSelectStep = () => (
    <div className="bulk-select-step" data-testid="bulk-select-step">
      <div
        className={`bulk-drop-zone ${isDragging ? 'drop-zone-active' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="bulk-drop-zone"
      >
        <div className="upload-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
        </div>
        <p className="upload-label">Drag &amp; drop compliance documents here</p>
        <p className="upload-hint">or click to browse — PDF, JPG, PNG up to 20MB each</p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          onChange={handleFileInput}
          style={{ display: 'none' }}
          data-testid="bulk-file-input"
        />
      </div>

      {files.length > 0 && (
        <div className="bulk-file-list" data-testid="bulk-file-list">
          <div className="bulk-file-list-header">
            <strong>{files.length} file{files.length !== 1 ? 's' : ''} selected</strong>
          </div>
          {files.map((f, idx) => (
            <div key={`${f.name}-${idx}`} className="bulk-file-row" data-testid={`bulk-file-row-${idx}`}>
              <span className="file-name">{f.name}</span>
              <span className="file-size">({(f.size / 1024).toFixed(0)} KB)</span>
              <button className="btn-remove-file" onClick={(e) => { e.stopPropagation(); removeFile(idx); }} data-testid={`remove-file-${idx}`}>&times;</button>
            </div>
          ))}
        </div>
      )}

      {error && <div className="upload-error" data-testid="bulk-upload-error">{error}</div>}

      <button
        className="btn-primary btn-upload"
        disabled={files.length === 0}
        onClick={handleBulkUpload}
        data-testid="bulk-upload-extract-btn"
      >
        Upload {files.length} File{files.length !== 1 ? 's' : ''} &amp; Extract with AI
      </button>
    </div>
  );

  // --- Render: Uploading ---
  const renderUploading = () => (
    <div className="bulk-uploading-step" data-testid="bulk-uploading-step">
      <div className="extracting-spinner"></div>
      <p className="extracting-label">Extracting data from {files.length} document{files.length !== 1 ? 's' : ''}...</p>
      <p className="extracting-hint">Claude is analysing each file. This may take a moment.</p>
      <div className="bulk-progress-list">
        {files.map((f, idx) => {
          const p = uploadProgress[idx] || { status: 'queued', pct: 0 };
          return (
            <div key={idx} className="bulk-progress-row" data-testid={`bulk-progress-${idx}`}>
              <span className="file-name">{f.name}</span>
              <div className="progress-bar-wrap">
                <div className="progress-bar-fill" style={{ width: `${p.pct}%` }}></div>
              </div>
              <span className={`progress-status progress-${p.status}`}>{p.status}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // --- Render: Review Table ---
  const renderReviewStep = () => (
    <div className="bulk-review-step" data-testid="bulk-review-step">
      <div className="bulk-review-header">
        <h3>Batch Review — {totalExtracted} Document{totalExtracted !== 1 ? 's' : ''}</h3>
        <p className="review-subtitle">
          {confirmedCount} of {totalExtracted} confirmed
          {needsAttentionCount > 0 && <span className="needs-attention"> — {needsAttentionCount} need attention</span>}
        </p>
      </div>

      <div className="bulk-review-table-wrap">
        <table className="bulk-review-table" data-testid="bulk-review-table">
          <thead>
            <tr>
              <th>Filename</th>
              <th>Matched Property</th>
              <th>Doc Type</th>
              <th>Expiry</th>
              <th>Confidence</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, idx) => {
              if (r.error || r.status === 'error') {
                return (
                  <tr key={idx} className="bulk-row-error" data-testid={`bulk-row-${idx}`}>
                    <td className="cell-bold">{r.filename}</td>
                    <td colSpan={5} className="cell-error-msg">{r.error}</td>
                    <td><span className="status-tag status-error">Error</span></td>
                  </tr>
                );
              }
              const row = editedRows[idx] || {};
              const isConfirmed = confirmedRows[idx];
              const propConf = r.fields?.property_id?.confidence || 'low';
              const dtConf = r.fields?.doc_type?.confidence || 'low';
              const missingProp = !row.property_id;
              const missingDocType = !row.doc_type;
              const rowClass = isConfirmed ? 'bulk-row-confirmed' : (missingProp || missingDocType) ? 'bulk-row-flagged' : 'bulk-row-pending';

              return (
                <tr key={idx} className={rowClass} data-testid={`bulk-row-${idx}`}>
                  <td className="cell-bold">{r.filename}</td>
                  <td>
                    <select
                      value={row.property_id || ''}
                      onChange={(e) => updateRow(idx, 'property_id', e.target.value)}
                      className={missingProp ? 'inline-select field-empty' : 'inline-select'}
                      disabled={isConfirmed}
                      data-testid={`bulk-property-${idx}`}
                    >
                      <option value="">-- Select --</option>
                      {propertyOptions.map(p => (
                        <option key={p.id} value={p.id}>{p.address}</option>
                      ))}
                    </select>
                    <ConfBadge level={propConf} />
                  </td>
                  <td>
                    <select
                      value={row.doc_type || ''}
                      onChange={(e) => updateRow(idx, 'doc_type', e.target.value)}
                      className={missingDocType ? 'inline-select field-empty' : 'inline-select'}
                      disabled={isConfirmed}
                      data-testid={`bulk-doctype-${idx}`}
                    >
                      <option value="">-- Select --</option>
                      {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                    <ConfBadge level={dtConf} />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={row.expiry_date || ''}
                      onChange={(e) => updateRow(idx, 'expiry_date', e.target.value)}
                      className="inline-input"
                      disabled={isConfirmed}
                      data-testid={`bulk-expiry-${idx}`}
                    />
                  </td>
                  <td>
                    <ConfBadge level={propConf === 'high' && dtConf === 'high' ? 'high' : propConf === 'low' || dtConf === 'low' ? 'low' : 'medium'} />
                  </td>
                  <td>
                    {isConfirmed ? (
                      <span className="status-tag status-confirmed" data-testid={`bulk-status-confirmed-${idx}`}>Confirmed</span>
                    ) : missingProp || missingDocType ? (
                      <span className="status-tag status-flagged" data-testid={`bulk-status-flagged-${idx}`}>Needs input</span>
                    ) : (
                      <span className="status-tag status-pending" data-testid={`bulk-status-pending-${idx}`}>Review</span>
                    )}
                  </td>
                  <td>
                    <div className="bulk-actions-cell">
                      {isConfirmed ? (
                        <button className="btn-sm btn-secondary" onClick={() => unconfirmRow(idx)} data-testid={`bulk-unconfirm-${idx}`}>Edit</button>
                      ) : (
                        <button
                          className="btn-sm btn-success"
                          disabled={missingProp || missingDocType}
                          onClick={() => confirmRow(idx)}
                          data-testid={`bulk-confirm-${idx}`}
                        >
                          Confirm
                        </button>
                      )}
                      <button
                        className="btn-sm btn-reextract"
                        onClick={() => handleReExtractRow(idx)}
                        disabled={reExtractingRow === idx}
                        data-testid={`bulk-reextract-${idx}`}
                      >
                        {reExtractingRow === idx ? '...' : 'Re-run'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {error && <div className="upload-error" data-testid="bulk-save-error">{error}</div>}

      <div className="bulk-review-actions">
        <button className="btn-secondary" onClick={() => { setStep('select'); setFiles([]); setResults([]); }} data-testid="bulk-back-btn">
          Start Over
        </button>
        <button
          className="btn-primary btn-sm"
          style={{ background: '#0073EA', color: '#fff' }}
          onClick={confirmAllRows}
          disabled={needsAttentionCount > 0}
          data-testid="bulk-confirm-all-btn"
        >
          Confirm All ({totalExtracted - confirmedCount} remaining)
        </button>
        <button
          className="btn-primary btn-save"
          style={{ background: '#0073EA', color: '#fff' }}
          disabled={confirmedCount === 0}
          onClick={handleBulkSave}
          data-testid="bulk-save-btn"
        >
          Save {confirmedCount} Document{confirmedCount !== 1 ? 's' : ''}
        </button>
      </div>
    </div>
  );

  // --- Render: Done ---
  const renderDone = () => (
    <div className="done-step" data-testid="bulk-done-step">
      <div className="done-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>
      <h3>{saveResult?.total_saved || 0} Document{(saveResult?.total_saved || 0) !== 1 ? 's' : ''} Saved Successfully</h3>
      {saveResult?.total_errors > 0 && (
        <p className="save-errors-note">{saveResult.total_errors} file{saveResult.total_errors !== 1 ? 's' : ''} had errors and were not saved.</p>
      )}
      <div className="done-actions">
        <button className="btn-primary" onClick={() => { setStep('select'); setFiles([]); setResults([]); setSaveResult(null); }} data-testid="bulk-upload-more-btn">
          Upload More
        </button>
        <button className="btn-secondary" onClick={onClose} data-testid="bulk-close-btn">
          Close
        </button>
      </div>
    </div>
  );

  // --- Render: Saving spinner ---
  const renderSaving = () => (
    <div className="extracting-step" data-testid="bulk-saving-step">
      <div className="extracting-spinner"></div>
      <p className="extracting-label">Saving {confirmedCount} document{confirmedCount !== 1 ? 's' : ''}...</p>
    </div>
  );

  return (
    <div className="compliance-upload-panel bulk-upload-panel" data-testid="bulk-upload-panel">
      <div className="upload-panel-header">
        <h2>Bulk Upload Compliance Documents</h2>
        <button className="btn-close-panel" onClick={onClose} data-testid="bulk-close-panel-btn">&times;</button>
      </div>
      <div className="upload-panel-body">
        {step === 'select' && renderSelectStep()}
        {step === 'uploading' && renderUploading()}
        {step === 'review' && renderReviewStep()}
        {step === 'saving' && renderSaving()}
        {step === 'done' && renderDone()}
      </div>
    </div>
  );
}
